import { useEffect, useRef, useState } from "react";
import {
  DEFAULT_REACTION,
  REACTION_EMOJIS,
  reactionEntries,
} from "../../../constants/reactions";
import s from "./reactionBar.module.css";

/**
 * Reaction chips + floating emoji picker (Telegram-style).
 */
export default function ReactionBar({
  reactions,
  myReaction,
  onReact,
  disabled = false,
  className = "",
  align = "start",
  size = "md",
  alwaysShowAdd = false,
}) {
  const [open, setOpen] = useState(false);
  const [popEmoji, setPopEmoji] = useState(null);
  const rootRef = useRef(null);
  const entries = reactionEntries(reactions);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!popEmoji) return undefined;
    const t = window.setTimeout(() => setPopEmoji(null), 420);
    return () => window.clearTimeout(t);
  }, [popEmoji]);

  if (!onReact) return null;

  const react = (emoji) => {
    if (disabled) return;
    setPopEmoji(emoji);
    onReact(emoji);
    setOpen(false);
  };

  const showAdd = alwaysShowAdd || open || entries.length === 0;

  return (
    <div
      ref={rootRef}
      className={[
        s.root,
        s[`size_${size}`],
        align === "end" ? s.alignEnd : s.alignStart,
        open ? s.rootOpen : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-reaction-bar=""
    >
      <div className={s.chips}>
        {entries.map(({ emoji, count }, index) => {
          const mine = myReaction === emoji;
          return (
            <button
              key={emoji}
              type="button"
              className={[
                s.chip,
                mine ? s.chipOn : "",
                popEmoji === emoji ? s.chipPop : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{ animationDelay: `${Math.min(index, 5) * 40}ms` }}
              onClick={() => react(emoji)}
              disabled={disabled}
              aria-pressed={mine ? "true" : "false"}
              aria-label={`Реакция ${emoji}${count > 1 ? `, ${count}` : ""}`}
            >
              <span className={s.chipEmoji} aria-hidden>
                {emoji}
              </span>
              <span className={s.chipCount}>{count}</span>
            </button>
          );
        })}
        <button
          type="button"
          className={[s.addBtn, showAdd || entries.length ? s.addBtnVisible : ""]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setOpen((v) => !v)}
          disabled={disabled}
          aria-expanded={open}
          aria-label="Добавить реакцию"
          title="Реакция"
        >
          <span className={s.addIcon} aria-hidden>
            {open ? "×" : "☺"}
          </span>
        </button>
      </div>

      {open ? (
        <div className={s.picker} role="listbox" aria-label="Выбор реакции">
          <div className={s.pickerInner}>
            {REACTION_EMOJIS.map((emoji, index) => (
              <button
                key={emoji}
                type="button"
                role="option"
                aria-selected={myReaction === emoji}
                className={[
                  s.pickerItem,
                  myReaction === emoji ? s.pickerItemOn : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{ animationDelay: `${index * 35}ms` }}
                onClick={() => react(emoji)}
                disabled={disabled}
              >
                <span className={s.pickerEmoji}>{emoji}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { DEFAULT_REACTION, REACTION_EMOJIS };
